import { useCallback, useEffect, useRef, useState } from "react";
import { toast } from "sonner";
import { Sparkles, RefreshCw } from "lucide-react";
import { Button } from "@/components/ui/button";
import {
  AlertDialog, AlertDialogAction, AlertDialogCancel, AlertDialogContent, AlertDialogDescription, AlertDialogFooter, AlertDialogHeader, AlertDialogTitle,
} from "@/components/ui/alert-dialog";
import { api, errMsg, rupee, istDateTime } from "@/lib/api";
import { AdvisorTrack } from "@/components/AdvisorTrack";
import { AdvisorChat } from "@/components/AdvisorChat";

const REGIME = {
  trending_up: ["Trending up", "text-emerald-300 bg-emerald-500/15"], trending_down: ["Trending down", "text-red-300 bg-red-500/15"],
  range: ["Range-bound", "text-sky-300 bg-sky-500/15"], volatile: ["Volatile", "text-amber-300 bg-amber-500/15"], unclear: ["Unclear", "text-slate-300 bg-slate-700/50"],
};

const Conf = ({ v }) => (
  <div className="flex items-center gap-1.5">
    <div className="h-1.5 w-16 rounded bg-slate-800"><div className="h-full rounded bg-indigo-400" style={{ width: `${v}%` }} /></div>
    <span className="num text-[10px] text-slate-400">{v}%</span>
  </div>
);

export const AdvisorPanel = ({ onApplied }) => {
  const [d, setD] = useState(null);
  const [busy, setBusy] = useState(false);
  const [confirm, setConfirm] = useState(false);
  const [showAvoid, setShowAvoid] = useState(false);
  const timer = useRef(null);
  const load = useCallback(async () => {
    try {
      const r = (await api.get("/advisor/latest")).data;
      setD(r);
      clearTimeout(timer.current);
      if (r.latest?.status === "running") timer.current = setTimeout(load, 3000);
    } catch { /* keep last */ }
  }, []);
  useEffect(() => { load(); const id = setInterval(load, 60000); return () => { clearInterval(id); clearTimeout(timer.current); }; }, [load]);
  const run = async () => {
    setBusy(true);
    try { await api.post("/advisor/run"); toast.info("AI Advisor is reading the market…"); load(); } catch (e) { toast.error(errMsg(e)); } finally { setBusy(false); }
  };
  const apply = async () => {
    try { await api.post("/advisor/apply", { id: d.advice.id }); toast.success("Advisor picks enabled for auto-trading"); onApplied?.(); load(); }
    catch (e) { toast.error(errMsg(e)); } finally { setConfirm(false); }
  };
  const latest = d?.latest, ok = d?.advice, a = ok?.advice;
  const running = latest?.status === "running";
  const [rl, rc] = REGIME[a?.regime] || REGIME.unclear;
  const applied = a?.picks?.length > 0 && d?.enabled?.length === a.picks.length && a.picks.every((p) => d.enabled.includes(p.kind));
  const stale = ok && ok.date !== new Date().toLocaleDateString("en-CA", { timeZone: "Asia/Kolkata" });
  return (
    <div className="panel col-span-12 p-4" data-testid="panel-ai-advisor">
      <div className="flex flex-wrap items-center gap-2">
        <span className="eyebrow flex items-center gap-1.5"><Sparkles size={12} className="text-indigo-300" /> AI Advisor · strategy for today</span>
        {a && <span className={`rounded px-1.5 py-0.5 text-[10px] font-semibold ${rc}`} data-testid="advisor-regime">{rl} · {a.regime_confidence}%</span>}
        {ok && <span className="text-[11px] text-slate-500" data-testid="advisor-meta">{ok.source === "auto" ? "Auto 09:45" : ok.source === "midday" ? "Midday 12:30" : ok.source === "periodic" ? "Autopilot 30-min check" : "Checked"} {istDateTime(ok.finished_at || ok.created_at)}{stale ? " (not today)" : ""}</span>}
        <div className="ml-auto flex items-center gap-2">
          {a?.picks?.length > 0 && !stale && (
            <Button data-testid="button-advisor-apply" size="sm" disabled={running || applied} onClick={() => setConfirm(true)} className="h-7 bg-indigo-500 text-xs hover:bg-indigo-400">
              {applied ? "Applied ✓" : "Apply picks"}
            </Button>
          )}
          <Button data-testid="button-advisor-recheck" size="sm" variant="outline" disabled={busy || running} onClick={run} className="h-7 gap-1 border-slate-700 text-xs">
            <RefreshCw size={12} className={running ? "animate-spin" : ""} /> {running ? "Analysing…" : "Re-check now"}
          </Button>
        </div>
      </div>
      {ok?.flipped && !stale && (
        <div data-testid="advisor-flip-banner" className="mt-3 flex flex-wrap items-center gap-2 rounded-md border border-red-500/50 bg-red-500/10 px-3 py-2 text-xs text-red-200">
          <span className="font-bold tracking-wide">REGIME FLIPPED</span>
          <span>Morning call {(REGIME[ok.prev_regime] || REGIME.unclear)[0]} → now <b>{rl}</b>. The picks below are updated for the new conditions.</span>
          {!applied && <Button data-testid="button-advisor-flip-apply" size="sm" onClick={() => setConfirm(true)} className="ml-auto h-6 bg-red-500 px-2 text-[11px] hover:bg-red-400">Apply new picks</Button>}
        </div>
      )}
      {latest?.status === "error" && <p className="mt-2 text-xs text-amber-400" data-testid="advisor-error">Last check failed: {latest.error}</p>}
      {!ok && !running && <p className="mt-3 text-sm text-slate-500" data-testid="advisor-empty">No advice yet. It runs automatically at 09:45 and 12:30 IST on trading days, or tap Re-check now.</p>}
      {running && !ok && <div className="mt-3 h-16 animate-pulse rounded bg-slate-900/60" />}
      {a && (
        <div className="mt-3 grid grid-cols-1 gap-4 lg:grid-cols-5">
          <div className="lg:col-span-2">
            <p className="text-sm leading-relaxed text-slate-300" data-testid="advisor-summary">{a.summary}</p>
            {a.watch?.length > 0 && (
              <ul className="mt-2 space-y-0.5 text-[11px] text-slate-400" data-testid="advisor-watch">
                {a.watch.map((w) => <li key={w}>• {w}</li>)}
              </ul>
            )}
          </div>
          <div className="space-y-2 lg:col-span-3">
            {a.picks.map((p, i) => (
              <div key={p.kind} data-testid={`advisor-pick-${p.kind}`} className="rounded-md border border-indigo-500/30 bg-indigo-500/[0.05] p-2.5">
                <div className="flex flex-wrap items-center gap-2">
                  <span className="text-[10px] font-semibold text-indigo-300">PICK {i + 1}</span>
                  <span className="font-semibold text-slate-100">{p.label}</span>
                  <Conf v={p.confidence} />
                  {p.margin != null && <span className="num ml-auto text-[11px] text-slate-500">margin {rupee(p.margin)}</span>}
                </div>
                <p className="mt-1 text-xs text-slate-300">{p.reason}</p>
              </div>
            ))}
            {a.avoid?.length > 0 && (
              <button data-testid="button-advisor-avoid" onClick={() => setShowAvoid((v) => !v)} className="text-[11px] text-slate-500 hover:text-slate-300">
                {showAvoid ? "Hide" : "Show"} {a.avoid.length} strategies to avoid today
              </button>
            )}
            {showAvoid && (
              <ul className="space-y-1 text-[11px]" data-testid="advisor-avoid-list">
                {a.avoid.map((x) => <li key={x.kind} className="text-slate-400"><span className="text-red-300">{x.label}:</span> {x.reason}</li>)}
              </ul>
            )}
          </div>
        </div>
      )}
      <AdvisorChat />
      <AdvisorTrack refresh={ok?.id} />
      <p className="mt-3 text-[10px] text-slate-600">Claude Sonnet 4.5 decision support, not a guarantee. Apply only switches which strategies auto-trade; all risk limits, gates and arming stay as they are.</p>
      <AlertDialog open={confirm} onOpenChange={setConfirm}>
        <AlertDialogContent data-testid="dialog-advisor-confirm" className="border-slate-700 bg-[#0D121F] text-slate-100">
          <AlertDialogHeader>
            <AlertDialogTitle>Auto-trade {a?.picks.map((p) => p.label).join(" + ")}?</AlertDialogTitle>
            <AlertDialogDescription className="text-slate-400">
              This replaces your enabled strategy list with the advisor's picks and switches every other strategy off. If live trading is armed, these will place REAL Dhan orders on their own signals, within your existing limits.
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel data-testid="button-advisor-cancel" className="border-slate-700 bg-transparent">Cancel</AlertDialogCancel>
            <AlertDialogAction data-testid="button-advisor-confirm" onClick={apply} className="bg-indigo-500 hover:bg-indigo-400">Apply picks</AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </div>
  );
};
